import { BookOpen, Copy, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PayinQrPanel } from "@/modules/dashboard/payins/components/PayinQrPanel";
import { usePayinBrebOrigin } from "@/modules/dashboard/payins/hooks/usePayinBrebOrigin";
import { buildLedgerPath } from "@/modules/dashboard/payments-ledger/utils/ledgerLinks";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { TransferReceiptActions } from "@/shared/components/payments/TransferReceiptActions";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { splitFeeTotal } from "@/shared/utils/paymentTaxes";
import { formatRail } from "@/shared/utils/railDisplay";
import { colocaToast } from "@/shared/utils/toast";

import { useOperationDetail } from "../hooks/useOperationDetail";
import type { ActivityRow } from "../types/paymentsActivity.types";
import { amountRowsView } from "../utils/amountRowsView";
import { ActivityStatusPill } from "./ActivityStatusPill";
import { OperationTimeline } from "./OperationTimeline";

export interface OperationDrawerProps {
  row: ActivityRow;
  open: boolean;
  onClose: () => void;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

/**
 * A money row. `value` is the runtime's decimal string; a null one renders as an em dash rather than "0.00",
 * because a payin that has not settled has no net yet and a fabricated zero reads as a real amount.
 */
function AmountRow({
  label,
  value,
  currency,
  locale,
  emphasize,
}: {
  label: string;
  value: string | null | undefined;
  currency: string;
  locale: string;
  emphasize?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {value != null ? (
        <ColocaAmountDisplay
          value={value}
          currency={currency}
          locale={locale}
          minimumFractionDigits={2}
          maximumFractionDigits={8}
          size="sm"
          className={cn("text-foreground", emphasize && "font-semibold")}
        />
      ) : (
        <span className="font-mono text-sm text-foreground tabular-nums">—</span>
      )}
    </div>
  );
}

function TextRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm break-words text-foreground">{value}</span>
    </div>
  );
}

function MetaRow({ label, value, copyable }: { label: string; value: string | null; copyable?: boolean }) {
  const { t } = useTranslations("paymentsActivity");
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1">
        <span className="truncate font-mono text-xs text-foreground" title={value}>
          {value}
        </span>
        {copyable && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={t("copy_reference_aria")}
            onClick={() => {
              void navigator.clipboard
                .writeText(value)
                .then(() => colocaToast.success(t("copy_success")))
                .catch(() => undefined);
            }}
          />
        )}
      </span>
    </div>
  );
}

/**
 * Read-only detail drawer of one runtime operation. While non-terminal it polls `GET /:id` every 6 s (via
 * `useOperationDetail`); amounts render gross → fees → net straight from the entity's money strings.
 * Provider-neutral: the external id is an opaque "Referencia" and `provider_code` is never rendered.
 */
export function OperationDrawer({ row: initialRow, open, onClose }: OperationDrawerProps) {
  const { t, activeLanguage } = useTranslations("paymentsActivity");
  const taxLabel = useTaxLabel();
  const { t: tPayins } = useTranslations("payins");
  const { isInternalUser, user } = useAuth();
  const navigate = useNavigate();
  const { row, timeline, isTimelineLoading } = useOperationDetail(initialRow);
  const origin = usePayinBrebOrigin(row.breb);
  const { payerView, creditsNothing } = amountRowsView(row.direction, row.status);
  const fee = splitFeeTotal(row.feeTotal, row.taxes);

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="md"
      title={row.direction === "payins" ? t("drawer_title_payin") : t("drawer_title_payout")}
      description={`${row.country} · ${formatRail(row.rail)} · ${row.currency}`}
      // Pinned: the comprobante is why most people open a past payout. Collections keep their own document.
      footer={
        row.direction === "payouts" ? (
          <TransferReceiptActions
            payoutId={row.id}
            status={row.status}
            organizationName={isInternalUser ? null : (user?.organization?.name ?? null)}
          />
        ) : undefined
      }
    >
      {/* Header — status + headline amount */}
      <ColocaSurface variant="muted" padding="md" className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <ActivityStatusPill row={row} />
          <ColocaBadge variant="secondary" className="font-mono text-[10px]">
            {row.status}
          </ColocaBadge>
        </div>
        <p className="mt-3 font-mono text-2xl font-bold tracking-tight text-foreground tabular-nums">
          {row.direction === "payins" ? "+" : "−"}
          <ColocaAmountDisplay
            value={row.amount}
            currency={row.currency}
            locale={activeLanguage}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
            size="lg"
          />
        </p>
        {row.failureReason && (
          <p className="mt-1 text-xs text-destructive">
            {t("drawer_failure_reason")}: {row.failureReason}
          </p>
        )}
      </ColocaSurface>

      {/* The QR of a collection still waiting to be paid. The runtime only serves `qr_code_payload` while the
          payin is CREATED and unexpired, so its presence IS the "may I still show this?" answer — no client-side
          status check to keep in sync with the backend gate. Above the amounts on purpose: someone who opens a
          pending collection came to get the payer paying, not to read fees. */}
      {row.qrCodePayload && (
        <div className="mb-4">
          <SectionTitle>{t("drawer_section_qr")}</SectionTitle>
          <PayinQrPanel
            payload={row.qrCodePayload}
            expiresAt={row.expiresAt}
            rail={row.rail}
            amount={`${formatCurrencyAmount(row.grossAmount ?? row.amount, row.currency, activeLanguage)} ${row.currency}`}
          />
        </div>
      )}

      {/* Amounts — gross → fees → net, straight from the entity's 8dp money strings */}
      <ColocaSurface padding="md" className="mb-4">
        <SectionTitle>{t("drawer_section_amounts")}</SectionTitle>
        <div className="divide-y divide-border/60">
          {payerView && <TextRow label={tPayins("payin_detail_channel")} value={origin.channel} />}
          {payerView && <TextRow label={tPayins("payin_detail_receiving_key")} value={origin.receivingKey} />}
          {payerView && <TextRow label={tPayins("payin_detail_payer")} value={origin.payer} />}
          <AmountRow
            label={payerView ? tPayins("payin_detail_sent") : t("drawer_amount_gross")}
            value={row.grossAmount}
            currency={row.currency}
            locale={activeLanguage}
          />
          <AmountRow
            label={payerView ? tPayins("payin_detail_fee") : t("drawer_amount_fees")}
            value={fee.commission}
            currency={row.currency}
            locale={activeLanguage}
          />
          {fee.taxes.map((tax) => (
            <AmountRow
              key={tax.code}
              label={taxLabel(tax)}
              value={tax.amount}
              currency={row.currency}
              locale={activeLanguage}
            />
          ))}
          {/* Net is what actually moves — never echo the gross as net while the runtime hasn't settled it. */}
          <AmountRow
            label={payerView ? tPayins("payin_detail_credited") : t("drawer_amount_net")}
            value={creditsNothing ? null : row.netAmount}
            currency={row.currency}
            locale={activeLanguage}
            emphasize
          />
        </div>
      </ColocaSurface>

      {/* Timeline — real transitions for INTERNAL, derived from timestamps for org members */}
      <ColocaSurface padding="md" className="mb-4">
        <SectionTitle>{t("drawer_section_timeline")}</SectionTitle>
        <OperationTimeline row={row} timeline={timeline} isTimelineLoading={isTimelineLoading} />
      </ColocaSurface>

      {/* The accounting half of this operation. INTERNAL only — the ledger endpoint is userTypes:[INTERNAL], so
          offering it to an org member would lead straight to a no-access view. */}
      {isInternalUser && (
        <div className="mb-4">
          <ColocaButton
            variant="default"
            size="sm"
            icon={BookOpen}
            onClick={() => navigate(buildLedgerPath(row.direction, row.id))}
          >
            {t("drawer_view_ledger")}
          </ColocaButton>
        </div>
      )}

      {/* Meta */}
      <ColocaSurface padding="md">
        <SectionTitle>{t("drawer_section_details")}</SectionTitle>
        <div className="divide-y divide-border/60">
          <MetaRow label={t("drawer_meta_id")} value={row.id} copyable />
          <MetaRow label={t("drawer_meta_reference")} value={row.providerReference} copyable />
          <MetaRow label={t("drawer_meta_concept")} value={row.concept} />
          <MetaRow label={t("drawer_meta_beneficiary")} value={row.beneficiaryName} />
          <MetaRow label={t("drawer_meta_breb_key")} value={row.brebKey} />
          <MetaRow
            label={t("drawer_meta_breb_network_reference")}
            value={row.brebNetworkReference}
            copyable
          />
          <MetaRow label={t("drawer_meta_created")} value={formatDateTime(row.createdAt)} />
          {/* QR/transfer window expiry — only meaningful while the payin can still settle. */}
          <MetaRow
            label={t("drawer_meta_expires")}
            value={row.isNonTerminal && row.expiresAt ? formatDateTime(row.expiresAt) : null}
          />
          <MetaRow label={t("drawer_meta_updated")} value={formatDateTime(row.updatedAt)} />
          {row.evidenceUrl && (
            <div className="flex items-center justify-between gap-4 py-1.5">
              <span className="shrink-0 text-xs text-muted-foreground">{t("drawer_meta_evidence")}</span>
              <a
                href={row.evidenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {t("drawer_meta_evidence_open")}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </ColocaSurface>
    </ColocaDrawer>
  );
}
